import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("ref") || params.get("session_id");
  const [state, setState] = useState("checking");
  const [memberId, setMemberId] = useState(null);
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (!res.ok) throw new Error("not found");
        const data = await res.json();
        if (data.payment_status === "paid") {
          setMemberId(data.membership_id || null);
          setProvider(data.provider || null);
          setState("paid");
          return;
        }
      } catch {
        // keep polling
      }
      tries += 1;
      if (tries >= 20) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(34,211,238,0.12),transparent)]" />
      <section data-testid="payment-success-page" className="relative z-10 mx-auto flex min-h-screen w-full max-w-2xl flex-col items-center justify-center px-6 py-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full rounded-3xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px]"
        >
          <div className="rounded-3xl bg-surface px-8 py-14 md:px-16">
            {state === "checking" && (
              <div data-testid="payment-checking">
                <Loader2 className="mx-auto h-12 w-12 animate-spin text-cyan-300" />
                <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">
                  Confirming your payment...
                </h1>
                <p className="mx-auto mt-4 max-w-md text-lg font-light leading-relaxed text-fog">
                  Hold on while we confirm the transaction.
                </p>
              </div>
            )}
            {state === "paid" && (
              <div data-testid="payment-paid">
                <CheckCircle2 className="mx-auto h-12 w-12 text-cyan-300" />
                <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight">
                  Backing confirmed. <span className="text-gradient">Welcome aboard.</span>
                </h1>
                <p className="mx-auto mt-4 max-w-md text-lg font-light leading-relaxed text-fog">
                  {provider === "invite"
                    ? "Your invite code covered the full amount. Your membership is active and the confirmation email with your card is on its way."
                    : "Your payment was successful. Your receipt is on its way to your email, along with full access to our papers, content, and videos as they are published."}
                </p>
                {memberId && (
                  <div className="mt-10">
                    <p className="font-mono text-base uppercase tracking-[0.25em] text-white/50">
                      Your Digital Founding Membership
                    </p>
                    <img
                      src={`${API}/membership/card/${memberId}`}
                      alt="Your Digital Founding Membership card"
                      data-testid="payment-membership-card"
                      className="mx-auto mt-4 w-full max-w-xl rounded-2xl border border-white/10 shadow-[0_16px_60px_rgba(59,130,246,0.18)]"
                    />
                    <p data-testid="payment-membership-id" className="text-gradient mt-5 font-mono text-base font-semibold tracking-[0.2em]">
                      {memberId}
                    </p>
                    <a
                      href={`${API}/membership/card/${memberId}/download`}
                      data-testid="payment-card-download"
                      className="bg-gradient-green mt-6 inline-flex items-center gap-3 rounded-full px-8 py-3.5 text-base font-semibold text-black shadow-[0_8px_32px_rgba(74,222,128,0.30)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(163,230,53,0.45)]"
                    >
                      <Download className="h-4 w-4" />
                      Download Membership Card
                    </a>
                  </div>
                )}
                <div>
                  <Link
                    to="/"
                    data-testid="payment-success-home-link"
                    className="bg-gradient-accent mt-10 inline-flex items-center gap-3 rounded-full px-10 py-4 text-base font-semibold text-white transition-opacity duration-300 hover:opacity-90"
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            )}
            {(state === "timeout" || state === "failed") && (
              <div data-testid="payment-unconfirmed">
                <XCircle className="mx-auto h-12 w-12 text-rose-300" />
                <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">
                  We could not confirm the payment yet.
                </h1>
                <p className="mx-auto mt-4 max-w-md text-lg font-light leading-relaxed text-fog">
                  If your card was charged, your receipt will still arrive by email.
                  Otherwise write to us at{" "}
                  <a href="mailto:invest@thelifespanproject.com" className="text-gradient link-underline">
                    invest@thelifespanproject.com
                  </a>
                  .
                </p>
                <Link
                  to="/subscribe"
                  data-testid="payment-retry-link"
                  className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/20 px-10 py-4 text-base font-medium text-white transition-colors duration-300 hover:border-cyan-300/50 hover:text-cyan-200"
                >
                  Back to Subscribe
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </section>
    </main>
  );
}
